import React from "react";
import PropTypes from "prop-types";
import { Icon, Spin, message } from "antd";
import PolicyList from "./policy-list";
import PolicyViewer from "./policy-viewer";
import PolicyHistory from "./policy-history";
import {
    listPolicies,
    fetchPolicy,
    fetchPolicyHistory,
    updatePolicy,
} from "services/policy-hub-service";
import "../../styles/policy-hub.css";

const TAB_DETAILS = "details";
const TAB_HISTORY = "history";

/**
 * Policy Hub — an app-wide reference and edit surface for lending policies.
 *
 * Data flow: list on mount; select a code (from the list or the URL param) to
 * fetch the full policy + its history. Edits mutate a local rows array which
 * is PUT back atomically — the backend replaces `content.rows` and writes a
 * PolicyChangeLog entry in the same transaction, so the history view is
 * consistent as soon as we refresh it after a save.
 */
class PolicyHub extends React.Component {
    state = {
        policies: [],
        canEdit: false,
        activeCode: null,
        activePolicy: null,
        history: [],
        loadingList: true,
        loadingDetail: false,
        loadingHistory: false,
        saving: false,
        tab: TAB_DETAILS,
    };

    componentDidMount() {
        this.loadList();
    }

    loadList = () => {
        this.setState({ loadingList: true });
        listPolicies()
            .then(res => {
                const results = (res && res.data && res.data.results) || [];
                const canEdit = Boolean(res && res.data && res.data.can_edit);
                this.setState({
                    policies: results,
                    canEdit,
                    loadingList: false,
                });
                // Auto-select the first policy on first load so the credit
                // team lands on data rather than an empty state.
                if (!this.state.activeCode && results.length > 0) {
                    this.selectPolicy(results[0].code);
                }
            })
            .catch(() => {
                message.error("Failed to load policies");
                this.setState({ loadingList: false });
            });
    };

    selectPolicy = code => {
        this.setState({
            activeCode: code,
            loadingDetail: true,
            tab: TAB_DETAILS,
            activePolicy: null,
            history: [],
        });
        fetchPolicy(code)
            .then(res => {
                const policy = res && res.data && res.data.policy;
                const canEdit = Boolean(res && res.data && res.data.can_edit);
                this.setState({
                    activePolicy: policy,
                    canEdit,
                    loadingDetail: false,
                });
            })
            .catch(() => {
                message.error("Failed to load policy");
                this.setState({ loadingDetail: false });
            });
    };

    loadHistory = () => {
        const { activeCode } = this.state;
        if (!activeCode) {
            return;
        }
        this.setState({ loadingHistory: true });
        fetchPolicyHistory(activeCode)
            .then(res => {
                this.setState({
                    history: (res && res.data && res.data.results) || [],
                    loadingHistory: false,
                });
            })
            .catch(() => {
                message.error("Failed to load history");
                this.setState({ loadingHistory: false });
            });
    };

    setTab = tab => {
        this.setState({ tab });
        if (tab === TAB_HISTORY) {
            this.loadHistory();
        }
    };

    handleRowsChange = nextRows => {
        const { activePolicy, activeCode } = this.state;
        if (!activePolicy || !activeCode) {
            return;
        }
        const payload = {
            content: { ...(activePolicy.content || {}), rows: nextRows },
        };
        this.setState({ saving: true });
        updatePolicy(activeCode, payload)
            .then(res => {
                const policy = res && res.data && res.data.policy;
                this.setState({
                    activePolicy: policy,
                    saving: false,
                });
                // Refresh the list too so the version number + last-updated
                // metadata in the sidebar stay accurate.
                this.loadList();
                message.success("Saved");
            })
            .catch(err => {
                const detail =
                    err &&
                    err.response &&
                    err.response.data &&
                    err.response.data.detail;
                message.error(detail || "Failed to save policy");
                this.setState({ saving: false });
            });
    };

    render() {
        const {
            policies,
            canEdit,
            activeCode,
            activePolicy,
            history,
            loadingList,
            loadingDetail,
            loadingHistory,
            saving,
            tab,
        } = this.state;

        return (
            <div className={"policy-hub"}>
                <PolicyList
                    policies={policies}
                    activeCode={activeCode}
                    onSelect={this.selectPolicy}
                />
                <div className={"policy-hub__content"}>
                    <div className={"policy-hub__toolbar"}>
                        <div className={"policy-hub__toolbar-titles"}>
                            <div className={"policy-hub__toolbar-name"}>
                                {activePolicy
                                    ? activePolicy.name
                                    : "Policy Hub"}
                            </div>
                            {activePolicy ? (
                                <div className={"policy-hub__toolbar-desc"}>
                                    {activePolicy.description}
                                </div>
                            ) : null}
                        </div>
                        <div className={"policy-hub__toolbar-actions"}>
                            {activePolicy ? (
                                <span className={"policy-hub__meta-chip"}>
                                    v{activePolicy.version}
                                </span>
                            ) : null}
                            {canEdit ? (
                                <span
                                    className={
                                        "policy-hub__meta-chip policy-hub__meta-chip--muted"
                                    }
                                >
                                    <Icon
                                        type={"edit"}
                                        style={{ marginRight: 4 }}
                                    />
                                    Edit
                                </span>
                            ) : (
                                <span
                                    className={
                                        "policy-hub__meta-chip policy-hub__meta-chip--muted"
                                    }
                                >
                                    <Icon
                                        type={"eye"}
                                        style={{ marginRight: 4 }}
                                    />
                                    Read-only
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={"policy-hub__tabs"}>
                        <span
                            className={
                                "policy-hub__tab" +
                                (tab === TAB_DETAILS
                                    ? " policy-hub__tab--active"
                                    : "")
                            }
                            onClick={() => this.setTab(TAB_DETAILS)}
                        >
                            Details
                        </span>
                        <span
                            className={
                                "policy-hub__tab" +
                                (tab === TAB_HISTORY
                                    ? " policy-hub__tab--active"
                                    : "")
                            }
                            onClick={() => this.setTab(TAB_HISTORY)}
                        >
                            History
                        </span>
                    </div>

                    <div className={"policy-hub__body"}>
                        {loadingList && !activePolicy ? (
                            <div className={"policy-hub__empty"}>
                                <Spin tip={"Loading policies…"} />
                            </div>
                        ) : null}
                        {tab === TAB_DETAILS ? (
                            loadingDetail ? (
                                <div className={"policy-hub__empty"}>
                                    <Spin tip={"Loading policy…"} />
                                </div>
                            ) : (
                                <PolicyViewer
                                    policy={activePolicy}
                                    canEdit={canEdit}
                                    saving={saving}
                                    onRowsChange={this.handleRowsChange}
                                />
                            )
                        ) : loadingHistory ? (
                            <div className={"policy-hub__empty"}>
                                <Spin tip={"Loading history…"} />
                            </div>
                        ) : (
                            <PolicyHistory entries={history} />
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

PolicyHub.propTypes = {
    // Reserved for future route params.
    match: PropTypes.object,
};

export default PolicyHub;
